import * as Sentry from '@sentry/react';

import {
  CreateTravellerQueryResult,
  GetTravellerResult,
} from 'lib/types/create-traveller';
import { setCid, setSalesRegisterId, setTid } from 'state/slices/user';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { AppToastService } from 'components/Toast/AppToastService';
import { BookingGateway } from 'services/http/booking-gateway';
import { QueryError } from 'lib/types/utility';
import { FlexibleDepartureDecision } from 'lib/types/product';
import { SelectedRoom } from 'lib/types/trip';
import { SelectionAvailability } from 'lib/types/availability';
import { checkTravellerData } from 'lib/helper/checkTravellerData';
import { getTravellerPayload } from 'lib/helper/traveller/traveller';
import { sentryService } from 'services/sentry/sentry';
import { sessionIdService } from '../../../services/session-id/session-id.service';
import { setInvoiceId } from 'state/slices/product';
import { stagePathConfig } from 'data/stage';
import { useCurrency } from 'lib/hooks/useCurrency';
import { useCustomNavigate } from '../../hooks/useCustomNavigate';
import { useRudderStack } from 'lib/hooks/useRudderStack';
import { useUserLocation } from 'lib/hooks/useUserLocation';
import { useFlexibleDeparturePolicy } from 'lib/hooks/useFlexibleDeparturePolicy';
import { useTenant } from 'lib/hooks/useTenant';

export type CreateTravellerPayload = {
  email: string;
  firstName: string;
  lastName: string;
  phonePrefix: string | undefined;
  phoneNumber: string;
  dateOfBirth: Date;
  flexibleDeparture?: boolean;
  flexibleDepartureDecision?: FlexibleDepartureDecision | null;
  selectedAvailability?: SelectionAvailability;
  selectedRooms: SelectedRoom[];
  /** Overrides the stored traveller count (DealsAway Rooms step). */
  travellersNumber?: number;
};

type Options = {
  /** Send the visitor back to Traveller when setup fails. */
  redirectToTravellerOnError?: boolean;
};

const useCreateTraveller = ({
  redirectToTravellerOnError = true,
}: Options = {}) => {
  const URL = '/public/booking/setup';
  const { isDA } = useTenant();
  const flexibleDeparturePolicy = useFlexibleDeparturePolicy();

  const flexibleDeparture = useAppSelector(
    (state) => state.product.flexibleDeparture
  );
  const flexibleDepartureDecision = useAppSelector(
    (state) => state.product.flexibleDepartureDecision
  );
  const queryClient = useQueryClient();
  const { customerRegistration } = useRudderStack();
  const { currency } = useCurrency();
  const location = useUserLocation();
  const countryName = location ? location.country.name : '';
  const navigate = useCustomNavigate();
  const duration = useAppSelector((state) => state.product.duration);
  const selectedDuration = duration?.id;
  const availability = useAppSelector((state) => state.product.availability);
  const dispatch = useAppDispatch();
  const travellerNumber = useAppSelector(
    (state) => state.product.travellerNumber
  );

  return useMutation<
    CreateTravellerQueryResult,
    QueryError,
    CreateTravellerPayload,
    unknown
  >(
    ['create-traveller'],
    async ({
      email,
      firstName,
      lastName,
      phonePrefix,
      phoneNumber,
      dateOfBirth,
      flexibleDeparture: flexibleDeparturePayload,
      flexibleDepartureDecision: flexibleDepartureDecisionPayload,
      selectedAvailability: selectedAvailabilityPayload,
      selectedRooms,
      travellersNumber: travellersNumberPayload,
    }: CreateTravellerPayload) => {
      const submittedFlexibleDeparture =
        flexibleDeparturePayload ?? flexibleDeparture;
      const payload = getTravellerPayload({
        selectedAvailability: selectedAvailabilityPayload ?? availability,
        selectedDuration,
        email,
        firstName,
        lastName,
        phonePrefix,
        phoneNumber,
        dateOfBirth,
        travellerNumber: travellersNumberPayload ?? travellerNumber,
        is_departure_flexible: submittedFlexibleDeparture,
        flexible_departure_decision: flexibleDeparturePolicy.getDecision(
          submittedFlexibleDeparture,
          flexibleDepartureDecisionPayload ?? flexibleDepartureDecision
        ),
        selectedRooms,
      });

      const response = await BookingGateway.post<CreateTravellerQueryResult>(
        URL,
        payload
      );

      // DealsAway calls setup twice (Traveller, then Rooms). Setup updates
      // the session product, so refresh it before the next stage reads it.
      if (isDA) {
        await queryClient.invalidateQueries({
          queryKey: ['product', sessionIdService.get()],
        });
      }

      return response;
    },
    {
      onSuccess(data, variables) {
        if (!checkTravellerData(data.result.customer)) {
          Sentry.captureException(
            `Incomplete traveller data was returned by createTraveller.`,
            {
              tags: {
                query: 'create traveller',
              },
              extra: {
                data,
              },
            }
          );
        }

        const travellerData: GetTravellerResult = {
          success: true,
          result: data.result.customer,
        };
        queryClient.setQueryData(
          ['get-traveller-info', sessionIdService.get()],
          travellerData
        );
        dispatch(setSalesRegisterId(data.result.customer.sales_register_id));
        dispatch(setCid(data.result.customer.cid));
        dispatch(setTid(data.result.customer.tid));
        dispatch(setInvoiceId(data.result.customer.invoice_id));
        queryClient.invalidateQueries({ queryKey: ['promotions'] });
        sentryService.setUser({
          id: data.result.cid ? String(data.result.cid) : undefined,
          email: variables?.email,
          username:
            variables?.firstName || variables?.lastName
              ? variables?.firstName + ' ' + variables?.lastName
              : undefined,
          extra: {
            salesRegisterId: data.result.sales_register_id
              ? String(data.result.sales_register_id)
              : undefined,
            tid: data.result.customer.tid
              ? String(data.result.customer.tid)
              : undefined,
            currency,
            location: countryName,
            sessionId: sessionIdService.get(),
          },
        });
        customerRegistration(
          variables.email,
          variables.firstName,
          variables.lastName,
          variables.phonePrefix
            ? variables.phonePrefix + variables.phoneNumber
            : variables.phoneNumber,
          data.result.cid,
          data.result.sales_register_id
        );
      },
      onError(error) {
        AppToastService.error(error?.message ?? 'Oops! Something went wrong!');
        if (redirectToTravellerOnError) {
          navigate(stagePathConfig.traveller);
        }
      },
    }
  );
};

export { useCreateTraveller };
